import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createMission,
  getMission,
  getMissionsPayload,
  isRequirementsReplaceBlocked,
  listMissionActions,
  listMissions,
  updateMission,
} from '../../services/missionsApi';
import { useMissionsSession } from './useMissionsSession';
import {
  COMPLETION_MODES,
  METRIC_TYPES,
  POOL_GROUPS,
  emptyMissionForm,
  emptyRequirement,
  findChainTail,
  formToMissionPayload,
  missionToForm,
  nextSortOrder,
  parseActionOptions,
} from './missionUtils';

function MissionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyMissionForm());
  const [actionOptions, setActionOptions] = useState([]);
  const [missionOptions, setMissionOptions] = useState([]);
  const [appendToChain, setAppendToChain] = useState(true);
  const [linkPreviousId, setLinkPreviousId] = useState('');
  const [requirementsBlocked, setRequirementsBlocked] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = useMissionsSession(setError);

  const loadData = useCallback(async () => {
    setError('');

    try {
      const [actionsRes, missionsRes] = await Promise.all([
        listMissionActions(),
        listMissions(),
      ]);

      if (handleApiResponse(actionsRes, 'Failed to load mission actions.')) {
        return;
      }
      setActionOptions(parseActionOptions(getMissionsPayload(actionsRes)));

      if (handleApiResponse(missionsRes, 'Failed to load missions.')) {
        return;
      }
      const missionsData = getMissionsPayload(missionsRes);
      const list = missionsData?.missions || missionsData || [];
      const missions = Array.isArray(list) ? list : [];
      setMissionOptions(missions);

      if (!isNew) {
        setLoading(true);
        const missionRes = await getMission(id);
        if (handleApiResponse(missionRes, 'Failed to load mission.')) {
          return;
        }
        const missionData = getMissionsPayload(missionRes);
        const mission = missionData?.mission || missionData;
        setForm(missionToForm(mission));
      } else {
        const tail = findChainTail(missions);
        setForm((prev) => ({ ...prev, sort_order: nextSortOrder(missions) }));
        setLinkPreviousId(tail?.id ? String(tail.id) : '');
        setAppendToChain(!!tail);
      }
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load mission data.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, handleApiResponse]);

  useEffect(() => {
    if (id && !/^\d+$/.test(String(id))) {
      navigate('/admin/missions', { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateRequirement = (index, field, value) => {
    setForm((prev) => {
      const requirements = [...prev.requirements];
      requirements[index] = { ...requirements[index], [field]: value };
      return { ...prev, requirements };
    });
  };

  const addRequirement = () => {
    setForm((prev) => ({
      ...prev,
      requirements: [...prev.requirements, emptyRequirement(prev.requirements.length + 1)],
    }));
  };

  const removeRequirement = (index) => {
    setForm((prev) => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.stage.trim()) {
      setError('Stage and name are required.');
      return;
    }
    if (form.requirements.some((r) => !r.action_key)) {
      setError('Every requirement needs an action key.');
      return;
    }

    setSaving(true);
    setError('');
    setRequirementsBlocked(false);

    try {
      const payload = formToMissionPayload(form);
      const result = isNew
        ? await createMission(payload)
        : await updateMission(id, payload);

      if (!isNew && isRequirementsReplaceBlocked(result)) {
        setRequirementsBlocked(true);
        handleApiResponse(result, 'Failed to save mission.');
        return;
      }

      if (handleApiResponse(result, 'Failed to save mission.')) {
        return;
      }

      const data = getMissionsPayload(result);
      const savedId = data?.mission?.id || data?.id || id;

      if (isNew && appendToChain && linkPreviousId && savedId) {
        const chainRes = await updateMission(linkPreviousId, {
          next_mission_id: Number(savedId),
        });
        if (handleApiResponse(chainRes, 'Mission created, but linking the previous mission failed.')) {
          setSnackbar({
            open: true,
            message: 'Mission created, but the previous mission was not chained. Edit that mission’s next_mission_id.',
            severity: 'warning',
          });
          setTimeout(() => {
            navigate(savedId ? `/admin/missions/${savedId}` : '/admin/missions');
          }, 900);
          return;
        }
      }

      setSnackbar({ open: true, message: isNew ? 'Mission created.' : 'Mission updated.', severity: 'success' });
      setTimeout(() => {
        navigate(savedId ? `/admin/missions/${savedId}` : '/admin/missions');
      }, 600);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save mission.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateAndSave = async () => {
    if (isNew) return;
    setSaving(true);
    setError('');
    try {
      const payload = formToMissionPayload({ ...form, is_active: false });
      const result = await updateMission(id, payload);
      if (handleApiResponse(result, 'Failed to save mission after deactivating.')) {
        return;
      }
      setForm((prev) => ({ ...prev, is_active: false }));
      setRequirementsBlocked(false);
      setSnackbar({
        open: true,
        message: 'Tasks saved. Mission is inactive — reactivate when ready.',
        severity: 'success',
      });
    } catch (saveError) {
      setError(saveError.message || 'Failed to save mission.');
    } finally {
      setSaving(false);
    }
  };

  const showModeFields =
    form.completion_mode === 'any_n_of_m' || form.completion_mode === 'streak_plus_any_n';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton component={Link} to="/admin/missions">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1 }}>
          {isNew ? 'Create Mission' : `Edit Mission #${id}`}
        </Typography>
      </Box>

      {error && !requirementsBlocked && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isNew && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Replacing tasks is rejected while this mission is active and assigned. Unchanged tasks are
          skipped by the backend. To change tasks: deactivate, save, then reactivate.
        </Alert>
      )}

      {requirementsBlocked && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" disabled={saving} onClick={handleDeactivateAndSave}>
              Deactivate &amp; save tasks
            </Button>
          }
        >
          {error || 'Cannot replace requirements while users have this mission assigned.'}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Mission Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stage"
                value={form.stage}
                onChange={(e) => updateField('stage', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                value={form.purpose}
                onChange={(e) => updateField('purpose', e.target.value)}
                margin="dense"
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={form.image_url}
                onChange={(e) => updateField('image_url', e.target.value)}
                margin="dense"
              />
              {form.image_url ? (
                <Box
                  component="img"
                  src={form.image_url}
                  alt="Mission"
                  sx={{ mt: 1, maxHeight: 120, maxWidth: 240, objectFit: 'cover', borderRadius: 1 }}
                />
              ) : null}
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={form.sort_order}
                onChange={(e) => updateField('sort_order', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Reward FE"
                value={form.reward_fe}
                onChange={(e) => updateField('reward_fe', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Next Mission"
                value={form.next_mission_id}
                onChange={(e) => updateField('next_mission_id', e.target.value)}
                margin="dense"
              >
                <MenuItem value="">None (end of chain)</MenuItem>
                {missionOptions
                  .filter((m) => isNew || String(m.id) !== String(id))
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      #{m.sort_order} — {m.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Completion Mode"
                value={form.completion_mode}
                onChange={(e) => updateField('completion_mode', e.target.value)}
                margin="dense"
              >
                {COMPLETION_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {showModeFields && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Required Count"
                  value={form.required_count}
                  onChange={(e) => updateField('required_count', e.target.value)}
                  margin="dense"
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Required Streak Days (legacy)"
                value={form.required_streak_days}
                onChange={(e) => updateField('required_streak_days', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => updateField('is_active', e.target.checked)}
                  />
                }
                label="Active (inactive missions are not assigned to new users)"
              />
            </Grid>
            {isNew && (
              <Grid item xs={12} md={8}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={appendToChain}
                      onChange={(e) => setAppendToChain(e.target.checked)}
                    />
                  }
                  label="Append to chain (set previous mission’s next_mission_id after create)"
                />
                {appendToChain && (
                  <TextField
                    fullWidth
                    select
                    label="Previous mission"
                    value={linkPreviousId}
                    onChange={(e) => setLinkPreviousId(e.target.value)}
                    margin="dense"
                  >
                    <MenuItem value="">None</MenuItem>
                    {missionOptions.map((m) => (
                      <MenuItem key={m.id} value={String(m.id)}>
                        #{m.sort_order} — {m.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Requirements
            </Typography>
            <Button startIcon={<AddIcon />} onClick={addRequirement} size="small">
              Add Task
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Metric</TableCell>
                <TableCell>Pool</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Distinct Dim.</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.requirements.map((req, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={req.action_key}
                      onChange={(e) => updateRequirement(index, 'action_key', e.target.value)}
                    >
                      <MenuItem value="">Select action</MenuItem>
                      {actionOptions.map((action) => (
                        <MenuItem key={action.action_key} value={action.action_key}>
                          {action.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={req.target_value}
                      onChange={(e) => updateRequirement(index, 'target_value', e.target.value)}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={req.metric_type}
                      onChange={(e) => updateRequirement(index, 'metric_type', e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      {METRIC_TYPES.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={req.pool_group}
                      onChange={(e) => updateRequirement(index, 'pool_group', e.target.value)}
                      sx={{ minWidth: 110 }}
                    >
                      {POOL_GROUPS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={req.sort_order}
                      onChange={(e) => updateRequirement(index, 'sort_order', e.target.value)}
                      sx={{ width: 70 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={req.distinct_dimension}
                      onChange={(e) => updateRequirement(index, 'distinct_dimension', e.target.value)}
                      placeholder="optional"
                      sx={{ minWidth: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeRequirement(index)}
                      disabled={form.requirements.length <= 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Mission' : 'Save Changes'}
        </Button>
        <Button component={Link} to="/admin/missions">
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default MissionEditor;
